import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, TrendingUp, Shield } from 'lucide-react';

const StarsAndBeyond = () => {
  // Define a common animation for the icons (rolling on their own axis with flash)
  const iconSpinAnimation = {
    rotateY: [0, 90, 180, 270, 360], // Keyframes for rotation
    filter: [
      'drop-shadow(0 0 5px rgba(255, 255, 0, 0.8))', // Flash at 0deg (front)
      'drop-shadow(0 0 0px rgba(255, 255, 0, 0))',   // Off at 90deg
      'drop-shadow(0 0 0px rgba(255, 255, 0, 0))',   // Off at 180deg (back)
      'drop-shadow(0 0 0px rgba(255, 255, 0, 0))',   // Off at 270deg
      'drop-shadow(0 0 5px rgba(255, 255, 0, 0.8))', // Flash at 360deg (front again)
    ],
  };

  // Define a common transition for the icons (rolling with flash)
  const iconSpinTransition = {
    duration: 5, // Duration for one full rotation and flash cycle
    ease: "linear", // Constant speed for rotation
    repeat: Infinity, // Loop indefinitely
    times: [0, 0.25, 0.5, 0.75, 1], // Control when keyframes occur (0%, 25%, 50%, 75%, 100%)
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.9 }}
      className="relative rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12 overflow-hidden" // Removed bg-gray-900/80 backdrop-blur-lg
    >
      {/* Background video from Cloudinary */}
      <div className="absolute inset-0 z-0">
        <video
          className="w-full h-full object-cover opacity-30"
          autoPlay
          loop
          muted
          playsInline
          src="https://res.cloudinary.com/dv1ald7mk/video/upload/v1700000000/space_background_loop.mp4" // Placeholder video
        ></video>
      </div>
      {/* Starfield overlay */}
      <div className="absolute inset-0 z-10">
        {Array.from({ length: 50 }).map((_, i) => {
          const size = Math.random() * 1.5;
          const animationDuration = 8 + Math.random() * 15;
          return (
            <motion.div
              key={i}
              className="absolute bg-white rounded-full"
              style={{
                width: size,
                height: size,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5 + 0.5,
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.8, 1, 0.8],
              }}
              transition={{
                duration: animationDuration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      <div className="relative z-20 text-center">
        <h2 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-500 mb-8">
          Stars & Beyond: Your Financial Universe
        </h2>
        <p className="text-xl text-gray-200 mb-10 max-w-4xl mx-auto">
          Beyond just credit repair, we empower you with the knowledge and tools to navigate your entire financial universe. Explore advanced strategies and secure your long-term prosperity.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="relative p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center" // Removed bg-gray-900/70
          >
            {/* Neon Glow Effect for each feature card */}
            <motion.div
              className="absolute inset-0 rounded-2xl" // No negative margin, just inset
              style={{
                background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', // Pearl white gradient
                filter: 'blur(8px)', // Soft glow
                zIndex: -1, // Behind the main content
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', // Mask to create border effect
                WebkitMaskComposite: 'xor', // Combine masks
                maskComposite: 'exclude', // Standard for exclude
              }}
              animate={{ opacity: [0.2, 0.5, 0.2] }} // Slow flicker
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="mb-4" // Container for the icon
              animate={iconSpinAnimation} // Apply the rotation and flash animation
              transition={iconSpinTransition} // Apply the rotation and flash transition
            >
              <Lightbulb className="text-yellow-400" size={48} />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Smart Investing Tips</h3>
            <p className="text-gray-300">Learn how to make your money work for you and build wealth over time.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="relative p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center" // Removed bg-gray-900/70
          >
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(8px)', zIndex: -1, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
              animate={{ opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="mb-4" // Container for the icon
              animate={iconSpinAnimation} // Apply the rotation and flash animation
              transition={iconSpinTransition} // Apply the rotation and flash transition
            >
              <TrendingUp className="text-yellow-400" size={48} />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Advanced Budgeting</h3>
            <p className="text-gray-300">Master your cash flow with sophisticated budgeting techniques.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="relative p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center" // Removed bg-gray-900/70
          >
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(8px)', zIndex: -1, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
              animate={{ opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="mb-4" // Container for the icon
              animate={iconSpinAnimation} // Apply the rotation and flash animation
              transition={iconSpinTransition} // Apply the rotation and flash transition
            >
              <Shield className="text-yellow-400" size={48} />
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Financial Security</h3>
            <p className="text-gray-300">Protect your assets and plan for a secure financial future.</p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default StarsAndBeyond;